import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useApi } from "./AuthContext";
import { Users, Clock, Plus, Pencil, Trash2, ChevronLeft, ChevronRight, X, KeyRound, ToggleLeft, ToggleRight, ShieldCheck, Phone, MapPin, Mail, DollarSign, Download } from "lucide-react";
import { toast } from "sonner";
import BackButton from "./BackButton";
import CurrencyInput from "./CurrencyInput";
import { formatUSD } from "../utils/currency";
import { downloadCsv, isoDate } from "../utils/csv";
import useIsMobile from "../utils/useIsMobile";

const WORKFORCE_ROLES = [
  { value: "admin", label: "Admin (Full Web Access)" },
  { value: "operador", label: "Operator" },
  { value: "operador_gerente", label: "Operator / Manager" },
  { value: "financeiro", label: "Financial" },
  { value: "field_operator", label: "Field Operator (PIN only)" },
];

const PROFESSIONAL_TITLES = ["Owner", "Operations Manager", "Foreman", "Crew Lead", "Landscaper", "Crew Member"];

const TABS = [
  { id: "members", label: "Team Members", icon: Users },
  { id: "timesheets", label: "Timesheets & Payroll", icon: Clock },
];

// Get the Sunday→Saturday week range containing a given date
function getWeekRange(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0 = Sun
  const start = new Date(d);
  start.setDate(d.getDate() - day);
  const end = new Date(start);
  end.setDate(start.getDate() + 7);
  return { start, end };
}

function fmtDateInput(d) {
  return d.toISOString().slice(0, 10);
}

function fmtDateLabel(d) {
  // Accept either Date or ISO string — internal callsites pass both.
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return String(d || "");
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  } catch { return iso; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  } catch { return iso; }
}

function isoToLocalDatetime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

function localDatetimeToIso(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}


export default function WorkforceModule({ readOnly = false }) {
  const api = useApi();
  const [tab, setTab] = useState("members");
  const isMobile = useIsMobile();

  // Members
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [editingMember, setEditingMember] = useState(null); // member object or { _new: true }

  // Timesheets
  const today = useMemo(() => new Date(), []);
  const [weekRef, setWeekRef] = useState(today);
  const { start: weekStart, end: weekEnd } = useMemo(() => getWeekRange(weekRef), [weekRef]);
  const [entries, setEntries] = useState([]);
  const [payroll, setPayroll] = useState(null);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null); // entry or { _new: true }
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  // Bumped whenever a timesheet is saved/deleted so the EmployeeTimecardModal
  // (which keeps its own day-by-day data state) re-fetches and shows the
  // recomputed Net Hours / Subtotal without a manual page reload.
  const [timesheetsRefreshKey, setTimesheetsRefreshKey] = useState(0);
  const bumpTimesheets = useCallback(() => {
    setTimesheetsRefreshKey((k) => k + 1);
    fetchTimesheets();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const { data } = await api.get("/admin/workforce/members?include_inactive=true");
      setMembers(data);
    } catch (e) { toast.error("Failed to load members"); }
    finally { setLoadingMembers(false); }
  }, [api]);

  const fetchTimesheets = useCallback(async () => {
    setLoadingEntries(true);
    try {
      const startIso = weekStart.toISOString();
      const endIso = weekEnd.toISOString();
      const [{ data: entriesData }, { data: payrollData }] = await Promise.all([
        api.get(`/admin/workforce/timesheets?start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}`),
        api.get(`/admin/workforce/payroll/by-employee?start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}`),
      ]);
      setEntries(entriesData);
      setPayroll(payrollData);
    } catch (e) { toast.error("Failed to load timesheets"); }
    finally { setLoadingEntries(false); }
  }, [api, weekStart, weekEnd]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);
  useEffect(() => { if (tab === "timesheets") fetchTimesheets(); }, [tab, fetchTimesheets]);

  return (
    <div className="space-y-4" data-testid="workforce-module">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <Users className="w-6 h-6 text-forest-700" /> Workforce
          </h2>
          <p className="text-sm text-stone-500 mt-1">Manage your team, track hours, and run payroll.</p>
        </div>
      </div>

      <div className={`flex gap-2 border-b border-stone-200 ${isMobile ? "overflow-x-auto -mx-4 px-4 pb-1" : ""}`}>
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors min-h-[44px] ${
                tab === t.id
                  ? "border-forest-600 text-forest-800"
                  : "border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "members" && (
        <MembersTab
          members={members}
          loading={loadingMembers}
          isMobile={isMobile}
          onAddMember={() => setEditingMember({ _new: true })}
          onEditMember={(m) => setEditingMember(m)}
          readOnly={readOnly}
        />
      )}

      {tab === "timesheets" && (
        <TimesheetsTab
          entries={entries}
          payroll={payroll}
          loading={loadingEntries}
          isMobile={isMobile}
          weekStart={weekStart}
          weekEnd={weekEnd}
          onPrevWeek={() => { const d = new Date(weekRef); d.setDate(d.getDate() - 7); setWeekRef(d); }}
          onNextWeek={() => { const d = new Date(weekRef); d.setDate(d.getDate() + 7); setWeekRef(d); }}
          onThisWeek={() => setWeekRef(new Date())}
          onNewEntry={() => setEditingEntry({ _new: true })}
          onEditEntry={(e) => setEditingEntry(e)}
          onOpenEmployee={(uid) => setSelectedEmployeeId(uid)}
          readOnly={readOnly}
        />
      )}

      {selectedEmployeeId && (
        <EmployeeTimecardModal
          employeeId={selectedEmployeeId}
          initialWeekRef={weekRef}
          refreshSignal={timesheetsRefreshKey}
          onClose={() => setSelectedEmployeeId(null)}
          onEditEntry={(e) => setEditingEntry(e)}
          onChanged={bumpTimesheets}
          readOnly={readOnly}
        />
      )}

      {editingMember && (
        <MemberEditor
          member={editingMember}
          onClose={() => setEditingMember(null)}
          onSaved={() => { setEditingMember(null); fetchMembers(); }}
        />
      )}

      {editingEntry && (
        <TimesheetEditor
          entry={editingEntry}
          members={members.filter(m => m.is_active !== false)}
          onClose={() => setEditingEntry(null)}
          onSaved={() => { setEditingEntry(null); bumpTimesheets(); }}
        />
      )}
    </div>
  );
}

// ============================== MEMBERS TAB ==============================

function MembersTab({ members, loading, isMobile, onAddMember, onEditMember, readOnly }) {
  return (
    <div className="space-y-3" data-testid="members-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-stone-500">{members.length} member{members.length !== 1 ? "s" : ""}</p>
        {!readOnly && (
          <button
            data-testid="add-member-btn"
            onClick={onAddMember}
            className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-3.5 py-2.5 rounded-xl text-sm font-semibold shadow-sm min-h-[44px]"
          >
            <Plus className="w-4 h-4" /> Add Team Member
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-stone-400 py-12 text-center">Loading...</p>
      ) : members.length === 0 ? (
        <p className="text-sm text-stone-400 py-12 text-center">No team members yet</p>
      ) : (
        <div className={`grid gap-3 ${isMobile ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"}`}>
          {members.map((m) => (
            <MemberCard key={m._id} member={m} onEdit={() => onEditMember(m)} readOnly={readOnly} />
          ))}
        </div>
      )}
    </div>
  );
}

function MemberCard({ member, onEdit, readOnly }) {
  const photo = member.profile_photo;
  const initials = (member.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div
      data-testid={`member-card-${member._id}`}
      className={`bg-white rounded-2xl border p-4 shadow-sm flex gap-3 ${member.is_active === false ? "border-stone-200 opacity-60" : "border-stone-200"}`}
    >
      <div className="w-12 h-12 rounded-full bg-forest-100 text-forest-700 flex items-center justify-center font-bold text-base flex-shrink-0 overflow-hidden">
        {photo ? <img src={photo} alt={member.name} className="w-full h-full object-cover" /> : initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-stone-900 truncate">{member.name}</p>
            <p className="text-xs text-stone-500 truncate">{member.professional_title || WORKFORCE_ROLES.find(r => r.value === member.role)?.label || member.role}</p>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            {member.role === "field_operator" && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${member.has_pin ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                <KeyRound className="w-3 h-3" /> {member.has_pin ? "PIN set" : "No PIN"}
              </span>
            )}
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${member.is_active === false ? "bg-stone-200 text-stone-600" : "bg-green-100 text-green-700"}`}>
              {member.is_active === false ? "Inactive" : "Active"}
            </span>
          </div>
        </div>
        <div className="mt-2 space-y-0.5 text-[11px] text-stone-500">
          {member.email && <p className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" /> {member.email}</p>}
          {member.phone && <p className="flex items-center gap-1 truncate"><Phone className="w-3 h-3" /> {member.phone}</p>}
          {member.address && <p className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3" /> {member.address}</p>}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-sm font-bold text-forest-700 flex items-center gap-1">
            <DollarSign className="w-3.5 h-3.5" /> {formatUSD(member.hourly_rate || 0)}/hr
          </span>
          {!readOnly && (
            <button
              data-testid={`edit-member-${member._id}`}
              onClick={onEdit}
              className="text-xs text-forest-700 hover:text-forest-800 font-semibold inline-flex items-center gap-1 min-h-[44px] px-2"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MemberEditor({ member, onClose, onSaved }) {
  const api = useApi();
  const isNew = !!member._new;
  const [form, setForm] = useState({
    name: member.name || "",
    email: member.email || "",
    password: "",
    role: member.role || "field_operator",
    professional_title: member.professional_title || "",
    phone: member.phone || "",
    address: member.address || "",
    profile_photo: member.profile_photo || "",
    drivers_license: member.drivers_license || "",
    emergency_contact_name: member.emergency_contact_name || "",
    emergency_contact_phone: member.emergency_contact_phone || "",
    hourly_rate: member.hourly_rate || 0,
    default_lunch_break: member.default_lunch_break != null ? member.default_lunch_break : 30,
    auto_deduct_lunch: member.auto_deduct_lunch !== false,
    pin: "",
    is_active: member.is_active !== false,
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (!form.email.trim()) { toast.error("Email required"); return; }
    if (isNew && (!form.password || form.password.length < 6)) { toast.error("Password required (min 6 chars)"); return; }
    if (form.pin && !/^\d{4}$/.test(form.pin)) { toast.error("PIN must be 4 digits"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (!payload.pin) delete payload.pin;
      if (isNew) {
        await api.post("/admin/users", payload);
        toast.success(`${payload.name} added`);
      } else {
        delete payload.email; // not editable to keep auth simple
        await api.put(`/admin/users/${member._id}`, payload);
        toast.success("Member updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${form.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${member._id}`);
      toast.success("Member deleted");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="member-editor">
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <BackButton onClick={onClose} testId="member-editor-back" />
            <h3 className="text-lg font-bold text-stone-900">{isNew ? "Add Team Member" : "Edit Member"}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Full Name *"><input data-testid="m-name" value={form.name} onChange={e => update("name", e.target.value)} required className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" /></Field>
          <Field label="Email *"><input type="email" data-testid="m-email" value={form.email} onChange={e => update("email", e.target.value)} required disabled={!isNew} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm disabled:bg-stone-50" /></Field>
          {isNew && (
            <Field label="Password (admin login) *"><input type="password" minLength={6} data-testid="m-password" value={form.password} onChange={e => update("password", e.target.value)} required className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" placeholder="min 6 characters" /></Field>
          )}
          <Field label="Role *">
            <select data-testid="m-role" value={form.role} onChange={e => update("role", e.target.value)} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm">
              {WORKFORCE_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </Field>
          <Field label="Professional Title">
            <input list="prof-title-suggestions" data-testid="m-title" value={form.professional_title} onChange={e => update("professional_title", e.target.value)} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" />
            <datalist id="prof-title-suggestions">{PROFESSIONAL_TITLES.map(t => <option key={t} value={t} />)}</datalist>
          </Field>
          <Field label="Phone"><input data-testid="m-phone" value={form.phone} onChange={e => update("phone", e.target.value)} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" /></Field>
          <div className="md:col-span-2">
            <Field label="Address"><input data-testid="m-address" value={form.address} onChange={e => update("address", e.target.value)} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" /></Field>
          </div>
          <Field label="Hourly Rate (USD)"><CurrencyInput value={form.hourly_rate} onChange={(v) => update("hourly_rate", v)} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="m-hourly-rate" /></Field>
          <Field label="Default Lunch Break (minutes)">
            <input
              data-testid="m-default-lunch"
              type="number"
              min="0"
              max="180"
              step="5"
              value={form.default_lunch_break}
              onChange={(e) => update("default_lunch_break", Math.max(0, Math.min(180, Number(e.target.value) || 0)))}
              className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
            />
            <span className="block text-[11px] text-stone-500 mt-1">Auto-deducted from every closed shift unless toggled off below.</span>
          </Field>
          <div className="md:col-span-2">
            <button
              type="button"
              data-testid="m-auto-deduct-toggle"
              onClick={() => update("auto_deduct_lunch", !form.auto_deduct_lunch)}
              className="inline-flex items-center gap-2 text-sm font-semibold text-stone-700 min-h-[44px]"
            >
              {form.auto_deduct_lunch ? <ToggleRight className="w-7 h-7 text-forest-600" /> : <ToggleLeft className="w-7 h-7 text-stone-400" />}
              {form.auto_deduct_lunch ? `Auto-deduct ${form.default_lunch_break}m lunch on every shift` : "Auto-deduct lunch is OFF (manual entry required)"}
            </button>
          </div>
          <Field label="PIN (4 digits) — Field Login">
            <input data-testid="m-pin" inputMode="numeric" pattern="\d{4}" maxLength={4} value={form.pin} onChange={e => update("pin", e.target.value.replace(/\D/g, ""))} placeholder={member.has_pin ? "•••• (set, leave empty to keep)" : "Optional"} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm font-mono tracking-widest" />
          </Field>
          <Field label="Driver's License"><input data-testid="m-license" value={form.drivers_license} onChange={e => update("drivers_license", e.target.value)} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" /></Field>
          <Field label="Emergency Contact Name"><input data-testid="m-ec-name" value={form.emergency_contact_name} onChange={e => update("emergency_contact_name", e.target.value)} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" /></Field>
          <Field label="Emergency Contact Phone"><input data-testid="m-ec-phone" value={form.emergency_contact_phone} onChange={e => update("emergency_contact_phone", e.target.value)} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" /></Field>
          <div className="md:col-span-2 mt-2">
            <button type="button" onClick={() => update("is_active", !form.is_active)} data-testid="m-active-toggle" className="inline-flex items-center gap-2 text-sm font-semibold text-stone-700 min-h-[44px]">
              {form.is_active ? <ToggleRight className="w-7 h-7 text-green-600" /> : <ToggleLeft className="w-7 h-7 text-stone-400" />}
              {form.is_active ? "Active" : "Inactive"}
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-stone-200">
          {!isNew && <button type="button" onClick={handleDelete} className="text-red-600 hover:text-red-700 text-sm font-semibold inline-flex items-center gap-1 min-h-[44px]"><Trash2 className="w-4 h-4" /> Delete</button>}
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-semibold text-stone-700 bg-stone-100 hover:bg-stone-200 rounded-lg min-h-[44px]">Cancel</button>
            <button type="submit" disabled={saving} data-testid="m-save-btn" className="px-5 py-2.5 text-sm font-semibold text-white bg-forest-600 hover:bg-forest-700 rounded-lg disabled:opacity-50 min-h-[44px]">{saving ? "Saving..." : (isNew ? "Add Member" : "Save Changes")}</button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-stone-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

// ============================== TIMESHEETS TAB ==============================

function TimesheetsTab({ entries, payroll, loading, isMobile, weekStart, weekEnd, onPrevWeek, onNextWeek, onThisWeek, onNewEntry, onEditEntry, readOnly, onOpenEmployee }) {
  const weekEndDisplay = new Date(weekEnd);
  weekEndDisplay.setDate(weekEndDisplay.getDate() - 1); // inclusive display
  const totalHours = payroll?.total_hours || 0;
  const totalDollars = payroll?.total_subtotal || 0;
  const rows = payroll?.rows || [];

  return (
    <div className="space-y-4" data-testid="timesheets-tab">
      {/* Weekly summary widget */}
      <div className="bg-gradient-to-br from-forest-700 to-forest-800 text-white rounded-2xl p-4 shadow-md" data-testid="weekly-summary">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-forest-100 uppercase tracking-wide">Weekly Payroll</p>
          <div className="flex items-center gap-1.5">
            <button data-testid="prev-week-btn" onClick={onPrevWeek} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center min-h-[44px] min-w-[44px]"><ChevronLeft className="w-4 h-4" /></button>
            <button data-testid="this-week-btn" onClick={onThisWeek} className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold min-h-[36px]">Today</button>
            <button data-testid="next-week-btn" onClick={onNextWeek} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center min-h-[44px] min-w-[44px]"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
        <p className="text-sm text-forest-200 mb-3" data-testid="week-range-label">{fmtDateLabel(weekStart)} → {fmtDateLabel(weekEndDisplay)}</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white/10 rounded-xl p-3">
            <p className="text-[10px] uppercase tracking-wide text-forest-200 font-semibold">Total Hours</p>
            <p className="text-2xl font-bold mt-0.5" data-testid="total-hours">{totalHours.toFixed(2)} h</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3">
            <p className="text-[10px] uppercase tracking-wide text-forest-200 font-semibold flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Total Payroll</p>
            <p className="text-2xl font-bold mt-0.5" data-testid="total-payroll">{formatUSD(totalDollars)}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-bold text-stone-800">Payroll by Employee</h3>
          <p className="text-xs text-stone-500">Click an employee to open their full time card</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="export-payroll-csv"
            onClick={() => {
              if (!rows.length) { toast.info("Nothing to export"); return; }
              const start = isoDate(weekStart);
              const end = isoDate(weekEndDisplay);
              const headers = ["Employee", "Role", "Hourly Rate", "Default Lunch (min)", "Auto-deduct", "Shifts", "Open Shifts", "Total Hours", "Total Payroll"];
              const data = rows.map(r => [
                r.name, r.role, r.hourly_rate.toFixed(2), r.default_lunch_break,
                r.auto_deduct_lunch ? "yes" : "no", r.entries_count, r.open_shifts,
                r.hours.toFixed(2), r.subtotal.toFixed(2),
              ]);
              // Totals row
              data.push(["TOTAL", "", "", "", "", "", "", totalHours.toFixed(2), totalDollars.toFixed(2)]);
              downloadCsv(`gow-payroll-${start}_to_${end}.csv`, headers, data);
              toast.success("Payroll exported");
            }}
            className="inline-flex items-center gap-2 bg-white border border-stone-300 hover:bg-stone-50 text-stone-700 px-3.5 py-2.5 rounded-xl text-sm font-semibold shadow-sm min-h-[44px]"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
          {!readOnly && (
            <button data-testid="new-timesheet-btn" onClick={onNewEntry} className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-3.5 py-2.5 rounded-xl text-sm font-semibold shadow-sm min-h-[44px]">
              <Plus className="w-4 h-4" /> Manual Entry
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-stone-400 py-12 text-center">Loading...</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-stone-400 py-12 text-center">No employees yet</p>
      ) : isMobile ? (
        <div className="space-y-2.5" data-testid="payroll-cards">
          {rows.map((r) => (
            <EmployeePayrollCard key={r.user_id} row={r} onOpen={() => onOpenEmployee(r.user_id)} />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="payroll-table">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-stone-600 uppercase">Employee</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-stone-600 uppercase">Total Hours (Week)</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-stone-600 uppercase">Total Payroll (Week)</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-stone-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const initials = (r.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
                return (
                  <tr
                    key={r.user_id}
                    className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition-colors"
                    data-testid={`payroll-row-${r.user_id}`}
                    onClick={() => onOpenEmployee(r.user_id)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-forest-100 text-forest-700 flex items-center justify-center text-xs font-bold overflow-hidden flex-shrink-0">
                          {r.profile_photo ? <img src={r.profile_photo} alt={r.name} className="w-full h-full object-cover" /> : initials}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-stone-900 truncate">{r.name || "—"}</p>
                          <p className="text-[11px] text-stone-500">
                            {formatUSD(r.hourly_rate)}/hr · {r.auto_deduct_lunch ? `auto -${r.default_lunch_break}m lunch` : "no auto lunch"}
                            {r.open_shifts > 0 && <span className="ml-2 text-amber-700 font-semibold">· {r.open_shifts} open shift{r.open_shifts > 1 ? "s" : ""}</span>}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-stone-800 tabular-nums">{r.hours.toFixed(2)} h</td>
                    <td className="px-4 py-3 text-right font-bold text-forest-700 tabular-nums">{formatUSD(r.subtotal)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`open-timecard-${r.user_id}`}
                        onClick={(ev) => { ev.stopPropagation(); onOpenEmployee(r.user_id); }}
                        className="text-forest-700 hover:text-forest-800 inline-flex items-center gap-1 text-xs font-semibold min-h-[36px]"
                      >
                        <Clock className="w-3.5 h-3.5" /> View Time Card
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-stone-400 text-center pt-2">
        Showing {rows.length} employee{rows.length !== 1 ? "s" : ""} ·
        {entries.length} shift{entries.length !== 1 ? "s" : ""} this week
      </p>
    </div>
  );
}

function EmployeePayrollCard({ row, onOpen }) {
  const initials = (row.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <button
      onClick={onOpen}
      data-testid={`payroll-card-${row.user_id}`}
      className="w-full bg-white rounded-2xl border border-stone-200 p-4 shadow-sm flex items-center gap-3 text-left active:scale-[0.99] transition-transform"
    >
      <div className="w-11 h-11 rounded-full bg-forest-100 text-forest-700 flex items-center justify-center font-bold text-base overflow-hidden flex-shrink-0">
        {row.profile_photo ? <img src={row.profile_photo} alt={row.name} className="w-full h-full object-cover" /> : initials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-stone-900 truncate">{row.name || "—"}</p>
        <p className="text-[11px] text-stone-500">{formatUSD(row.hourly_rate)}/hr · auto -{row.default_lunch_break}m lunch</p>
      </div>
      <div className="text-right">
        <p className="text-sm font-bold text-stone-800 tabular-nums">{row.hours.toFixed(2)} h</p>
        <p className="text-sm font-bold text-forest-700 tabular-nums">{formatUSD(row.subtotal)}</p>
      </div>
    </button>
  );
}

function TimesheetEditor({ entry, members, onClose, onSaved }) {
  const api = useApi();
  const isNew = !!entry._new;
  const [form, setForm] = useState({
    user_id: entry.user_id || "",
    clock_in: isoToLocalDatetime(entry.clock_in || new Date().toISOString()),
    clock_out: isoToLocalDatetime(entry.clock_out || ""),
    break_minutes: entry.break_minutes || 0,
    notes: entry.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.user_id) { toast.error("Select a team member"); return; }
    if (!form.clock_in) { toast.error("Clock-in time required"); return; }
    setSaving(true);
    try {
      const payload = {
        user_id: form.user_id,
        clock_in: localDatetimeToIso(form.clock_in),
        clock_out: form.clock_out ? localDatetimeToIso(form.clock_out) : null,
        break_minutes: Number(form.break_minutes) || 0,
        notes: form.notes,
      };
      if (isNew) {
        await api.post("/admin/workforce/timesheets", payload);
        toast.success("Entry created");
      } else {
        await api.put(`/admin/workforce/timesheets/${entry.id}`, payload);
        toast.success("Entry updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this timesheet entry?")) return;
    try {
      await api.delete(`/admin/workforce/timesheets/${entry.id}`);
      toast.success("Entry deleted");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="timesheet-editor">
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <BackButton onClick={onClose} testId="ts-editor-back" />
            <h3 className="text-lg font-bold text-stone-900">{isNew ? "New Timesheet Entry" : "Edit Entry"}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <Field label="Team Member *">
            <select data-testid="ts-user-select" value={form.user_id} onChange={e => update("user_id", e.target.value)} required className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm">
              <option value="">— Select member —</option>
              {members.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
            </select>
          </Field>
          <Field label="Clock-in *"><input data-testid="ts-clock-in" type="datetime-local" value={form.clock_in} onChange={e => update("clock_in", e.target.value)} required className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" /></Field>
          <Field label="Clock-out (leave empty for open shift)"><input data-testid="ts-clock-out" type="datetime-local" value={form.clock_out} onChange={e => update("clock_out", e.target.value)} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" /></Field>
          <Field label="Break (minutes)"><input data-testid="ts-break" type="number" min="0" step="5" value={form.break_minutes} onChange={e => update("break_minutes", e.target.value)} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" /></Field>
          <Field label="Notes"><textarea data-testid="ts-notes" rows={2} value={form.notes} onChange={e => update("notes", e.target.value)} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm resize-none" /></Field>
        </div>
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-stone-200">
          {!isNew && <button type="button" onClick={handleDelete} className="text-red-600 hover:text-red-700 text-sm font-semibold inline-flex items-center gap-1 min-h-[44px]"><Trash2 className="w-4 h-4" /> Delete</button>}
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2.5 text-sm font-semibold text-stone-700 bg-stone-100 hover:bg-stone-200 rounded-lg min-h-[44px]">Cancel</button>
            <button type="submit" disabled={saving} data-testid="ts-save-btn" className="px-5 py-2.5 text-sm font-semibold text-white bg-forest-600 hover:bg-forest-700 rounded-lg disabled:opacity-50 min-h-[44px]">{saving ? "Saving..." : "Save"}</button>
          </div>
        </div>
      </form>
    </div>
  );
}



// ============================== EMPLOYEE TIME CARD MODAL ==============================

function EmployeeTimecardModal({ employeeId, initialWeekRef, refreshSignal = 0, onClose, onEditEntry, onChanged, readOnly }) {
  const api = useApi();
  // Date range is the source of truth. `endExclusive` follows backend convention
  // (the API uses [start, end) so the exclusive end is start of next day).
  const initialRange = useMemo(() => {
    const { start: s, end: e } = getWeekRange(initialWeekRef || new Date());
    return { start: s, endExclusive: e };
  }, [initialWeekRef]);
  const [range, setRange] = useState(initialRange);
  const { start, endExclusive } = range;
  const endDisplay = useMemo(() => {
    const d = new Date(endExclusive); d.setDate(d.getDate() - 1); return d;
  }, [endExclusive]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ---- Presets (operate on the start anchor and recompute the range) ----
  const setThisWeek = () => {
    const { start: s, end: e } = getWeekRange(new Date());
    setRange({ start: s, endExclusive: e });
  };
  const setLastWeek = () => {
    const ref = new Date(); ref.setDate(ref.getDate() - 7);
    const { start: s, end: e } = getWeekRange(ref);
    setRange({ start: s, endExclusive: e });
  };
  const setThisMonth = () => {
    const now = new Date();
    const s = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    const e = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0);
    setRange({ start: s, endExclusive: e });
  };
  const shiftRange = (days) => {
    const s = new Date(start); s.setDate(s.getDate() + days);
    const e = new Date(endExclusive); e.setDate(e.getDate() + days);
    setRange({ start: s, endExclusive: e });
  };

  // Detect which preset is currently active so we can highlight the button
  const presetKey = useMemo(() => {
    const sIso = isoDate(start);
    const eIso = isoDate(endDisplay);
    const thisWeek = getWeekRange(new Date());
    if (sIso === isoDate(thisWeek.start) && isoDate(new Date(thisWeek.end.getTime() - 86400000)) === eIso) return "this-week";
    const lastRef = new Date(); lastRef.setDate(lastRef.getDate() - 7);
    const lw = getWeekRange(lastRef);
    if (sIso === isoDate(lw.start) && isoDate(new Date(lw.end.getTime() - 86400000)) === eIso) return "last-week";
    const now = new Date();
    const ms = new Date(now.getFullYear(), now.getMonth(), 1);
    const me = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    if (sIso === isoDate(ms) && eIso === isoDate(me)) return "this-month";
    return "custom";
  }, [start, endDisplay]);

  // ---- Custom date pickers (yyyy-mm-dd inputs) ----
  const setStartDate = (yyyymmdd) => {
    if (!yyyymmdd) return;
    const [y, m, d] = yyyymmdd.split("-").map(Number);
    const s = new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
    // Keep end >= start
    let e = endExclusive;
    if (s.getTime() >= endExclusive.getTime()) {
      e = new Date(s); e.setDate(e.getDate() + 1);
    }
    setRange({ start: s, endExclusive: e });
  };
  const setEndDate = (yyyymmdd) => {
    if (!yyyymmdd) return;
    const [y, m, d] = yyyymmdd.split("-").map(Number);
    const eIncl = new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
    const e = new Date(eIncl); e.setDate(e.getDate() + 1); // exclusive
    // Keep start <= end
    let s = start;
    if (s.getTime() >= e.getTime()) {
      s = new Date(e); s.setDate(s.getDate() - 1);
    }
    setRange({ start: s, endExclusive: e });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const startIso = start.toISOString();
      const endIso = endExclusive.toISOString();
      const { data: timecard } = await api.get(
        `/admin/workforce/employee/${employeeId}/timecard?start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}`
      );
      setData(timecard);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load time card"); }
    finally { setLoading(false); }
  }, [api, employeeId, start, endExclusive]);

  useEffect(() => { load(); }, [load]);

  // Re-fetch silently when an external mutation happens (e.g., admin saves the
  // TimesheetEditor modal that sits on top of this one). The signal is bumped
  // by the parent WorkforceModule.
  const lastSignalRef = useRef(refreshSignal);
  useEffect(() => {
    if (refreshSignal !== lastSignalRef.current) {
      lastSignalRef.current = refreshSignal;
      load();
    }
  }, [refreshSignal, load]);

  const handleDelete = async (entryId) => {
    if (!window.confirm("Delete this shift?")) return;
    try {
      await api.delete(`/admin/workforce/timesheets/${entryId}`);
      toast.success("Shift deleted");
      load();
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const profile = data?.profile;
  const totals = data?.totals || { hours: 0, subtotal: 0, lunch_minutes: 0, entries_count: 0 };
  const days = data?.days || [];

  const fmtTimeForCsv = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
    } catch { return iso; }
  };

  const exportTimecard = () => {
    if (!profile || days.length === 0) { toast.info("Nothing to export"); return; }
    // Always use the CURRENT filter range, not whatever the API last returned.
    const sIso = isoDate(start);
    const eIso = isoDate(endDisplay);
    const headers = ["Date", "Clock-In", "Clock-Out", "Lunch (min)", "Override", "Net Hours", "Hourly Rate", "Subtotal"];
    const dataRows = [];
    for (const day of days) {
      for (const e of day.entries) {
        dataRows.push([
          day.date,
          fmtTimeForCsv(e.clock_in),
          e.is_open ? "IN PROGRESS" : fmtTimeForCsv(e.clock_out),
          e.break_minutes,
          e.break_override ? "yes" : "",
          (e.hours || 0).toFixed(2),
          (e.hourly_rate || profile.hourly_rate || 0).toFixed(2),
          (e.subtotal || 0).toFixed(2),
        ]);
      }
    }
    dataRows.push(["TOTAL", "", "", totals.lunch_minutes, "", totals.hours.toFixed(2), "", totals.subtotal.toFixed(2)]);
    const safeName = (profile.name || "employee").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    downloadCsv(`gow-timecard-${safeName}-${sIso}_to_${eIso}.csv`, headers, dataRows);
    toast.success("Time card exported");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm p-2 sm:p-4 overflow-y-auto" data-testid="employee-timecard-modal">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[94vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-stone-200 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <BackButton onClick={onClose} testId="timecard-back" />
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-stone-900 truncate" data-testid="timecard-employee-name">
                {loading && !profile ? "Loading…" : profile?.name || "—"}
              </h3>
              {profile && (
                <p className="text-xs text-stone-500">
                  {formatUSD(profile.hourly_rate)}/hr · {profile.auto_deduct_lunch ? `auto -${profile.default_lunch_break}m lunch` : "manual lunch"}
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700 p-1"><X className="w-5 h-5" /></button>
        </div>

        {/* Action row */}
        <div className="px-5 pt-3 flex justify-end">
          <button
            data-testid="export-timecard-csv"
            onClick={exportTimecard}
            disabled={loading || days.length === 0}
            className="inline-flex items-center gap-2 bg-white border border-stone-300 hover:bg-stone-50 text-stone-700 px-3.5 py-2 rounded-xl text-xs font-semibold shadow-sm min-h-[40px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>

        {/* Filters + totals */}
        <div className="bg-gradient-to-br from-forest-700 to-forest-800 text-white px-5 py-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <p className="text-xs font-semibold text-forest-100 uppercase tracking-wide">Time Card</p>
            <div className="flex items-center gap-1.5">
              <button
                data-testid="tc-prev-range"
                onClick={() => shiftRange(-7)}
                title="Shift range 7 days back"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center min-h-[44px] min-w-[44px]"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                data-testid="tc-next-range"
                onClick={() => shiftRange(7)}
                title="Shift range 7 days forward"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center min-h-[44px] min-w-[44px]"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Preset chips */}
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="tc-presets">
            {[
              { id: "this-week", label: "This Week", onClick: setThisWeek },
              { id: "last-week", label: "Last Week", onClick: setLastWeek },
              { id: "this-month", label: "This Month", onClick: setThisMonth },
            ].map((p) => {
              const active = presetKey === p.id;
              return (
                <button
                  key={p.id}
                  data-testid={`tc-preset-${p.id}`}
                  data-active={active ? "1" : "0"}
                  onClick={p.onClick}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold min-h-[36px] transition-colors ${
                    active ? "bg-white text-forest-800" : "bg-white/10 hover:bg-white/20 text-white"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
            {presetKey === "custom" && (
              <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 min-h-[36px] inline-flex items-center" data-testid="tc-preset-custom-badge">
                Custom Range
              </span>
            )}
          </div>

          {/* Custom date pickers */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <label className="flex flex-col text-[10px] uppercase tracking-wide text-forest-200 font-semibold">
              From
              <input
                data-testid="tc-date-from"
                type="date"
                value={isoDate(start)}
                max={isoDate(endDisplay)}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-sm text-white normal-case font-normal focus:outline-none focus:bg-white/20"
                style={{ colorScheme: "dark" }}
              />
            </label>
            <label className="flex flex-col text-[10px] uppercase tracking-wide text-forest-200 font-semibold">
              To
              <input
                data-testid="tc-date-to"
                type="date"
                value={isoDate(endDisplay)}
                min={isoDate(start)}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-sm text-white normal-case font-normal focus:outline-none focus:bg-white/20"
                style={{ colorScheme: "dark" }}
              />
            </label>
          </div>

          <p className="text-xs text-forest-200 mb-3" data-testid="tc-range-label">
            {fmtDateLabel(start)} → {fmtDateLabel(endDisplay)}
          </p>

          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-[10px] uppercase tracking-wide text-forest-200 font-semibold">Net Hours</p>
              <p className="text-xl font-bold mt-0.5 tabular-nums" data-testid="tc-total-hours">{totals.hours.toFixed(2)}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-[10px] uppercase tracking-wide text-forest-200 font-semibold">Lunch (min)</p>
              <p className="text-xl font-bold mt-0.5 tabular-nums">{totals.lunch_minutes}</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-[10px] uppercase tracking-wide text-forest-200 font-semibold">Subtotal</p>
              <p className="text-xl font-bold mt-0.5 tabular-nums" data-testid="tc-total-subtotal">{formatUSD(totals.subtotal)}</p>
            </div>
          </div>
        </div>

        {/* Daily breakdown */}
        <div className="p-4">
          {loading ? (
            <p className="text-sm text-stone-400 py-10 text-center">Loading time card…</p>
          ) : days.length === 0 ? (
            <p className="text-sm text-stone-400 py-10 text-center">No shifts this week</p>
          ) : (
            <div className="space-y-3" data-testid="tc-days">
              {days.map((day) => (
                <div key={day.date} className="bg-stone-50 rounded-2xl border border-stone-200 overflow-hidden" data-testid={`tc-day-${day.date}`}>
                  <div className="px-4 py-2.5 bg-white border-b border-stone-200 flex items-center justify-between">
                    <p className="text-sm font-bold text-stone-800">{fmtDateLabel(`${day.date}T12:00:00`)}</p>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-stone-500">Lunch: <strong className="text-stone-700 tabular-nums">{day.lunch_minutes}m</strong></span>
                      <span className="text-stone-500">Net: <strong className="text-stone-700 tabular-nums">{day.hours.toFixed(2)}h</strong></span>
                      <span className="font-bold text-forest-700 tabular-nums">{formatUSD(day.subtotal)}</span>
                    </div>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="bg-stone-100 text-stone-600 uppercase">
                      <tr>
                        <th className="px-3 py-2 text-left">Clock-In</th>
                        <th className="px-3 py-2 text-left">Clock-Out</th>
                        <th className="px-3 py-2 text-right">Lunch (min)</th>
                        <th className="px-3 py-2 text-right">Net Hours</th>
                        <th className="px-3 py-2 text-right">Subtotal</th>
                        <th className="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {day.entries.map((e) => (
                        <tr key={e.id} className="border-t border-stone-200 bg-white" data-testid={`tc-entry-${e.id}`}>
                          <td className="px-3 py-2 text-stone-700 tabular-nums">{fmtTime(e.clock_in)}</td>
                          <td className="px-3 py-2 text-stone-700 tabular-nums">{e.is_open ? <span className="text-amber-600 font-semibold">In progress</span> : fmtTime(e.clock_out)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <span className={e.break_override ? "text-amber-700 font-semibold" : "text-stone-700"}>
                              {e.break_minutes}
                            </span>
                            {e.break_override && <span className="ml-1 text-[9px] text-amber-700 font-bold">·EDIT</span>}
                          </td>
                          <td className="px-3 py-2 text-right text-stone-800 font-semibold tabular-nums">{(e.hours || 0).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right text-forest-700 font-bold tabular-nums">{formatUSD(e.subtotal || 0)}</td>
                          <td className="px-3 py-2 text-right">
                            {!readOnly && (
                              <div className="inline-flex gap-2">
                                <button
                                  data-testid={`tc-edit-${e.id}`}
                                  onClick={() => onEditEntry(e)}
                                  className="text-forest-700 hover:text-forest-800 inline-flex items-center gap-1 font-semibold min-h-[32px]"
                                >
                                  <Pencil className="w-3 h-3" /> Edit
                                </button>
                                <button
                                  data-testid={`tc-delete-${e.id}`}
                                  onClick={() => handleDelete(e.id)}
                                  className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 font-semibold min-h-[32px]"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
